import {
  APIEntityType,
  APIFieldType,
  APITagAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { logError, logWarn } from '@ardoq/logging';
import { fieldUtils } from '@ardoq/scope-data';
import {
  baseAttributesMap,
  componentAttributesMap,
  componentTypeAttributesMap,
  defaultAttributesMap,
  fieldAttributesMap,
  fieldTypeMap,
  modelAttributesMap,
  referenceAttributesMap,
  referenceTypeAttributesMap,
  tagAttributesMap,
} from '../fieldTypeMaps';
import { FieldType } from '../types';
import { getModelTypeDictionaryKey } from '../enhanceScopeData';
import {
  getDefaultAttributeLabel,
  getDefaultAttributeLabelForComponent,
  getDefaultAttributeLabelForReference,
} from '../../utils';
import {
  getModelIdFromReference,
  getModelIfFromSourceComponent,
} from './getModelIdFromReference';
import { type EnhancedScopeData, TAGS_FIELD_NAME } from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';

export const getFieldType = (
  entityType: APIEntityType,
  id: string | number,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => {
  switch (entityType) {
    case APIEntityType.COMPONENT:
      // Tags are a synthetic field surfaced as a component column but
      // not present in the model's fields, so they must be typed explicitly.
      if (name === TAGS_FIELD_NAME) {
        return FieldType.SELECT_MULTIPLE_LIST;
      }
      return getFieldTypeFromComponent(id as string, name, enhancedScopeData);

    case APIEntityType.REFERENCE:
      return getFieldTypeFromReference(id as string, name, enhancedScopeData);

    case APIEntityType.COMPONENT_TYPE:
      return getFieldTypeFromComponentType(id as string, name);

    case APIEntityType.REFERENCE_TYPE:
      return getFieldTypeFromReferenceType(id as string, name);

    case APIEntityType.FIELD:
      return getFieldTypeFromField(id as string, name, enhancedScopeData);

    case APIEntityType.MODEL:
      return getFieldTypeFromModel(name);

    case APIEntityType.TAG:
      return getFieldTypeFromTag(id as string, name as keyof APITagAttributes);
  }
};

const getFieldTypeFromModel = (name: string) => {
  return modelAttributesMap.get(name) || FieldType.NONE;
};

const getFieldTypeFromField = (
  id: ArdoqId,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => {
  if (
    name === 'defaultValue' &&
    enhancedScopeData.fieldsById[id].type === APIFieldType.DATE_ONLY_RANGE
  ) {
    return FieldType.DEFAULT_VALUE_DATE_ONLY_RANGE;
  }

  if (
    name === 'defaultValue' &&
    enhancedScopeData.fieldsById[id].type === APIFieldType.DATE_TIME_RANGE
  ) {
    return FieldType.DEFAULT_VALUE_DATE_TIME_RANGE;
  }

  const fieldType =
    fieldAttributesMap.get(name) ||
    defaultAttributesMap.get(name) ||
    baseAttributesMap.get(name);
  if (fieldType) return fieldType;
  return FieldType.NONE;
};

export const hasComponentField = (
  id: string,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => getFieldTypeFromComponent(id, name, enhancedScopeData) !== FieldType.NONE;

const getFieldTypeFromComponent = (
  id: string,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const component = enhancedScopeData.componentsById[id];

  if (!component) {
    logError(
      Error(
        'missing component in enhancedScopeData.componentsById in getFieldTypeFromComponent'
      )
    );
    return FieldType.NONE;
  }

  let fieldType = [
    baseAttributesMap,
    defaultAttributesMap,
    componentAttributesMap,
  ]
    .find(map => map.get(name))
    ?.get(name);
  if (fieldType) {
    return fieldType;
  }
  const key = getModelTypeDictionaryKey(component.model, component.typeId);
  const fieldsForThisModelIdAndTypeId =
    enhancedScopeData.fieldsByModelIdAndTypeId[key];
  const field = fieldsForThisModelIdAndTypeId?.[name];
  fieldType = field && fieldTypeMap.get(field.type);
  return fieldType ?? FieldType.NONE;
};

const getFieldTypeFromComponentType = (id: string, name: string) => {
  const fieldType = componentTypeAttributesMap.get(name);
  if (fieldType) return fieldType;

  logWarn(
    Error(
      `couldn't resolve component field type in getFieldTypeFromComponentType`
    ),
    null,
    { id }
  );

  return FieldType.NONE;
};

const getFieldTypeFromReferenceType = (id: string, name: string) => {
  const fieldType = referenceTypeAttributesMap.get(name);
  if (fieldType) return fieldType;

  logWarn(
    Error(
      `couldn't resolve reference field type in getFieldTypeFromReferenceType`
    ),
    null,
    { id }
  );

  return FieldType.NONE;
};
const getFieldTypeFromTag = (id: string, name: keyof APITagAttributes) => {
  const fieldType =
    tagAttributesMap.get(name) || defaultAttributesMap.get(name);
  if (fieldType) return fieldType;
  logWarn(
    Error(`couldn't resolve tag field type in getFieldTypeFromTag`),
    null,
    { id }
  );
  return FieldType.NONE;
};

export const hasReferenceField = (
  id: string,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => getFieldTypeFromReference(id, name, enhancedScopeData) !== FieldType.NONE;

const getFieldTypeFromReference = (
  id: string,
  name: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const reference = enhancedScopeData.referencesById[id];
  if (!reference) {
    logError(
      Error(
        'missing reference in enhancedScopeData.referencesById in getFieldTypeFromReference'
      )
    );
    return FieldType.NONE;
  }

  const fieldType = [
    baseAttributesMap,
    defaultAttributesMap,
    referenceAttributesMap,
  ]
    .find(map => map.get(name))
    ?.get(name);
  if (fieldType) {
    return fieldType;
  }
  const modelId =
    getModelIdFromReference(reference, enhancedScopeData) ??
    getModelIfFromSourceComponent(reference, enhancedScopeData);

  const key = getModelTypeDictionaryKey(modelId, String(reference.type));
  const fieldsForThisModelIdAndTypeId =
    enhancedScopeData.fieldsByModelIdAndTypeId[key];
  const field = fieldsForThisModelIdAndTypeId?.[name];

  return (field && fieldTypeMap.get(field.type)) ?? FieldType.NONE;
};

const fieldTypesWithOptions = new Set([
  APIFieldType.LIST,
  APIFieldType.SELECT_MULTIPLE_LIST,
]);

type TransposeArgs = {
  entityType: APIEntityType;
  fieldName: string;
  enhancedScopeData: EnhancedScopeData;
  entityId: ArdoqId;
};

const transposeMap = new Map([
  [
    'defaultValue',
    {
      transposedValue: 'listOptions',
      isTransposeCheck: ({
        enhancedScopeData,
        entityType,
        entityId,
      }: TransposeArgs) => {
        if (entityType !== APIEntityType.FIELD) {
          return false;
        }

        const fieldType = enhancedScopeData.fieldsById[entityId]?.type ?? null;

        return fieldType && fieldTypesWithOptions.has(fieldType);
      },
    },
  ],
]);

export const transposeFieldNameIfNeeded = (props: {
  entityType: APIEntityType;
  fieldName: string;
  enhancedScopeData: EnhancedScopeData;
  entityId: ArdoqId;
}) => {
  const { fieldName } = props;
  if (!transposeMap.has(fieldName)) {
    return fieldName;
  }
  const { transposedValue, isTransposeCheck } = transposeMap.get(fieldName)!;

  return isTransposeCheck(props) ? transposedValue : fieldName;
};

export const getFieldLabelForComponent = (
  scopeData: EnhancedScopeData,
  fieldName: string,
  i18nLocale?: I18nLocale
): string | undefined =>
  fieldUtils.getLabelByFieldName(scopeData, fieldName) ??
  getDefaultAttributeLabelForComponent(fieldName, i18nLocale);

export const getFieldLabelForReference = (
  scopeData: EnhancedScopeData,
  fieldName: string,
  i18nLocale?: I18nLocale
): string | undefined =>
  fieldUtils.getLabelByFieldName(scopeData, fieldName) ??
  getDefaultAttributeLabelForReference(fieldName, i18nLocale);

export const getFieldLabel = ({
  fieldName,
  enhancedScopeData,
  entityType,
  fallbackEnhancedScopeData,
  i18nLocale,
}: {
  entityType: APIEntityType;
  fieldName: string;
  enhancedScopeData: EnhancedScopeData;
  fallbackEnhancedScopeData?: EnhancedScopeData;
  i18nLocale?: I18nLocale;
}) => {
  // Short circuit lookup for entities without custom fields
  if (
    entityType === APIEntityType.FIELD ||
    entityType === APIEntityType.MODEL ||
    entityType === APIEntityType.COMPONENT_TYPE ||
    entityType === APIEntityType.REFERENCE_TYPE ||
    entityType === APIEntityType.TAG
  ) {
    return getDefaultAttributeLabel(fieldName, i18nLocale) || fieldName;
  } else if (entityType === APIEntityType.COMPONENT) {
    return (
      getFieldLabelForComponent(enhancedScopeData, fieldName, i18nLocale) ??
      fieldName
    );
  } else if (entityType === APIEntityType.REFERENCE) {
    return (
      getFieldLabelForReference(enhancedScopeData, fieldName, i18nLocale) ??
      fieldName
    );
  }

  const label =
    fieldUtils.getLabelByFieldName(enhancedScopeData, fieldName) ??
    (fallbackEnhancedScopeData &&
      fieldUtils.getLabelByFieldName(fallbackEnhancedScopeData, fieldName)) ??
    getDefaultAttributeLabel(fieldName, i18nLocale);

  if (!label) {
    logError(new Error(`Couldn't find field label`), null, {
      entityType,
    });
  }

  return label ?? fieldName;
};
